import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useResponsive } from '@/hooks/use-responsive';
import type { BlogPageDataResponse } from '@/services/cms/getBlogContent/schemas';
import formatDateString from '@/utils/dates';
import { useNavigate } from '@tanstack/react-router';

interface BlogFeaturePostProps {
  blogFeaturedData: BlogPageDataResponse;
  className?: string;
}

export const BlogFeaturedPost = ({
  blogFeaturedData,
  className,
}: BlogFeaturePostProps) => {
  const { isDesktop } = useResponsive();
  const { title, feature_image, date, slug } = blogFeaturedData;
  const postDate = formatDateString(date);
  const navigate = useNavigate();

  if (!blogFeaturedData) {
    return null;
  }

  return (
    <div className={cn('inline-flex flex-col gap-8 w-full', className)}>
      <div className="gap-6 md:gap-8 flex flex-col md:flex-row lg:flex-row w-full">
        <div className="w-full flex flex-col lg:w-max">
          <div className="flex flex-col gap-2 lg:gap-4 items-start w-full md:w-[336px] lg:w-[434px]">
            <Badge variant="black" size={isDesktop ? 'large' : 'medium'}>
              Featured Post
            </Badge>
            <div className="w-full">
              <h2 className="text-black text-xl md:text-2xl lg:text-[52px] lg:leading-[72px] font-nunito700 line-clamp-3 lg:line-clamp-4">
                {title}
              </h2>
            </div>
            <div className="text-dark-grey-300 text-sm font-light leading-tight mb-4 md:mb-6 lg:mb-[60px]">
              {postDate}
            </div>
          </div>
          <div className="mt-auto">
            <Button
              onClick={() => {
                navigate({
                  to: `/blog/${slug}`,
                });
              }}
              size={isDesktop ? 'medium' : 'small'}
            >
              Read More
            </Button>
          </div>
        </div>
        <div className="h-[178px] md:h-[207px] lg:h-[408px] rounded-3xl w-full">
          <img
            className="w-full h-full object-cover rounded-3xl"
            src={feature_image?.url}
            alt={feature_image?.alt ?? ''}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};
